import { useEffect, useMemo, useState } from "react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
    AreaChart, Area,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Users, Globe2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import MetricCard from "@/components/dashboard/MetricCard";
import UserWorldMap from "@/components/dashboard/UserWorldMap";
import SourceBadge from "@/components/dashboard/SourceBadge";

const nz = new Intl.NumberFormat("en-NZ");

export default function UsersDashboard() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [hovered, setHovered] = useState(null);
    const [countryFilter, setCountryFilter] = useState("ALL");

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/metrics/users");
                if (!cancel) setData(data);
            } catch (e) {
                if (!cancel) setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
            }
        })();
        return () => { cancel = true; };
    }, []);

    const regions = useMemo(() => {
        if (!data) return [];
        if (countryFilter === "ALL") return data.regions;
        return data.regions.filter((r) => r.country === countryFilter);
    }, [data, countryFilter]);

    if (err) {
        return (
            <div className="p-8">
                <div className="hg-card p-5 border-[#ef4444]/40">
                    <div className="hg-eyebrow text-[#fecaca]">// Error</div>
                    <div className="font-mono-data mt-1">{err}</div>
                </div>
            </div>
        );
    }
    if (!data) return <Skeleton />;

    const t = data.totals;
    const hoveredCluster = hovered && data.clusters.find((c) => c.region === hovered);

    return (
        <div className="min-h-full px-4 md:px-8 py-6 md:py-8" data-testid="users-dashboard">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pb-6 border-b border-white/5">
                <div>
                    <div className="hg-eyebrow mb-2">// Section 03 · Users</div>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#FAFAFA] leading-none">
                        Users Per Region
                    </h1>
                    <p className="mt-2 text-sm text-[#A1A1AA]">
                        Registered growers by city, signup velocity and global footprint.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <SourceBadge source={data.source} note={data.source_note} liveLabel="Live · Users DB" />
                    <CountryFilter value={countryFilter} onChange={setCountryFilter} />
                    <div className="hidden md:flex items-center gap-2 hg-eyebrow">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] hg-pulse-dot" />
                        Updated {new Date(data.updated_at).toLocaleTimeString()}
                    </div>
                </div>
            </header>

            {/* KPI strip */}
            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
                <MetricCard
                    label="// Total registered"
                    value={nz.format(t.total_users)}
                    accent
                    tag="ALL REGIONS"
                    testid="metric-total-users"
                >
                    <div className="flex gap-4 font-mono-data text-[11px] text-[#A1A1AA]">
                        <span>NZ <span className="text-[#FAFAFA]">{nz.format(t.nz_users)}</span></span>
                        <span>AU <span className="text-[#FAFAFA]">{nz.format(t.au_users)}</span></span>
                        <span>ROW <span className="text-[#FAFAFA]">{nz.format(t.other_users)}</span></span>
                    </div>
                </MetricCard>
                <MetricCard
                    label="// New this week"
                    value={nz.format(t.new_week)}
                    deltaPct={t.week_delta_pct}
                    deltaLabel="prev week"
                    testid="metric-new-week"
                />
                <MetricCard
                    label="// New this month"
                    value={nz.format(t.new_month)}
                    deltaPct={t.month_delta_pct}
                    deltaLabel="prev month"
                    testid="metric-new-month"
                />
                <div className="hg-card p-5 flex flex-col gap-2" data-testid="metric-signups-trend">
                    <div className="hg-eyebrow">// Daily signups · 30d</div>
                    <div className="h-[72px] -mx-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data.signups_daily} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="signupFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#22C55E" stopOpacity={0.45} />
                                        <stop offset="100%" stopColor="#22C55E" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <Tooltip content={<DarkTooltip fmt={(v) => `${Math.round(v)} signups`} />} />
                                <Area type="monotone" dataKey="value" stroke="#22C55E" strokeWidth={1.5} fill="url(#signupFill)" dot={false} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </section>

            {/* Bar chart + table */}
            <section className="grid grid-cols-1 lg:grid-cols-6 gap-4 mt-4">
                <div className="hg-card p-5 lg:col-span-3" data-testid="users-bar-chart">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="hg-eyebrow">// Users by city</div>
                            <div className="font-heading text-xl text-[#FAFAFA] mt-1">{regions.length} regions</div>
                        </div>
                        <Users size={14} className="text-[#22C55E]" />
                    </div>
                    <div className="h-[380px] mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={regions} layout="vertical" margin={{ top: 0, right: 48, left: 0, bottom: 0 }} barCategoryGap={6}>
                                <XAxis type="number" hide />
                                <YAxis
                                    type="category"
                                    dataKey="code"
                                    width={44}
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "JetBrains Mono, monospace" }}
                                />
                                <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} content={<DarkTooltip fmt={(v, p) => `${p.region} · ${nz.format(v)} users (${p.pct}%)`} />} />
                                <Bar
                                    dataKey="users"
                                    radius={[0, 2, 2, 0]}
                                    label={{ position: "right", fill: "#FAFAFA", fontSize: 10, fontFamily: "JetBrains Mono, monospace", formatter: (v) => nz.format(v) }}
                                    onMouseEnter={(d) => setHovered(d.region)}
                                    onMouseLeave={() => setHovered(null)}
                                >
                                    {regions.map((r) => (
                                        <Cell
                                            key={r.code}
                                            fill={r.country === "NZ" ? "#22C55E" : "#15803D"}
                                            fillOpacity={hovered && hovered !== r.region ? 0.35 : 1}
                                            style={{ filter: hovered === r.region ? "drop-shadow(0 0 8px #22C55E)" : "none" }}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-2 flex gap-4 hg-eyebrow text-[10px]">
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#22C55E]" /> NZ</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#15803D]" /> AU</span>
                    </div>
                </div>

                <div className="hg-card p-5 lg:col-span-3 overflow-x-auto" data-testid="users-table">
                    <div className="hg-eyebrow pb-3 border-b border-white/5">// Region breakdown</div>
                    <table className="w-full mt-1 text-left">
                        <thead>
                            <tr className="hg-eyebrow text-[9px]">
                                <th className="py-2 font-normal">#</th>
                                <th className="py-2 font-normal">Region</th>
                                <th className="py-2 font-normal text-right">Users</th>
                                <th className="py-2 font-normal text-right">Share</th>
                                <th className="py-2 font-normal text-right">Wk</th>
                                <th className="py-2 font-normal text-right">Mo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {regions.map((r, i) => (
                                <tr
                                    key={r.code}
                                    className={`border-t border-white/[0.04] transition-colors ${hovered === r.region ? "bg-[#22C55E]/[0.06]" : "hover:bg-white/[0.02]"}`}
                                    onMouseEnter={() => setHovered(r.region)}
                                    onMouseLeave={() => setHovered(null)}
                                    data-testid={`users-row-${r.code.toLowerCase()}`}
                                >
                                    <td className="py-2 font-mono-data text-[10px] text-[#52525b]">{String(i + 1).padStart(2, "0")}</td>
                                    <td className="py-2">
                                        <div className="font-mono-data text-xs text-[#FAFAFA]">{r.region}</div>
                                        <div className="hg-eyebrow text-[9px]">{r.country} · {r.code}</div>
                                    </td>
                                    <td className="py-2 text-right font-mono-data text-xs text-[#FAFAFA]">{nz.format(r.users)}</td>
                                    <td className="py-2 text-right">
                                        <div className="font-mono-data text-xs text-[#22C55E]">{r.pct}%</div>
                                        <div className="h-[2px] mt-1 ml-auto w-14 bg-white/5 rounded-full overflow-hidden">
                                            <div className="h-full bg-[#22C55E]" style={{ width: `${Math.min(100, r.pct * 4)}%` }} />
                                        </div>
                                    </td>
                                    <td className="py-2 text-right"><Delta n={r.new_week} pct={r.week_delta_pct} /></td>
                                    <td className="py-2 text-right"><Delta n={r.new_month} pct={r.month_delta_pct} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* World map */}
            <section className="mt-4">
                <div className="hg-card p-5" data-testid="users-world-map-card">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="hg-eyebrow">// Global footprint</div>
                            <div className="font-heading text-xl text-[#FAFAFA] mt-1">Pin clusters · sized by user density</div>
                        </div>
                        {hoveredCluster ? (
                            <div className="text-right">
                                <div className="hg-eyebrow">// {hoveredCluster.country} · {hoveredCluster.code}</div>
                                <div className="font-mono-data text-lg text-[#FAFAFA]">{hoveredCluster.region}</div>
                                <div className="font-mono-data text-xs text-[#22C55E]">{nz.format(hoveredCluster.users)} users</div>
                            </div>
                        ) : (
                            <Globe2 size={14} className="text-[#22C55E]" />
                        )}
                    </div>
                    <div className="relative mt-3 h-[300px] md:h-[480px] w-full">
                        <UserWorldMap clusters={data.clusters} onHover={setHovered} hovered={hovered} />
                    </div>
                </div>
            </section>
        </div>
    );
}

const Delta = ({ n, pct }) => {
    const up = pct >= 0;
    const Icon = up ? ArrowUpRight : ArrowDownRight;
    return (
        <div>
            <div className="font-mono-data text-xs text-[#FAFAFA]">+{nz.format(n)}</div>
            <div className={`flex items-center justify-end gap-0.5 font-mono-data text-[10px] ${up ? "text-[#22C55E]" : "text-[#ef4444]"}`}>
                <Icon size={10} />{Math.abs(pct).toFixed(1)}%
            </div>
        </div>
    );
};

const DarkTooltip = ({ active, payload, fmt }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="hg-glass px-3 py-2 rounded-sm border border-white/10">
            {p.date && <div className="hg-eyebrow text-[9px]">{p.date}</div>}
            <div className="font-mono-data text-xs text-[#FAFAFA]">{fmt(payload[0].value, p)}</div>
        </div>
    );
};

const CountryFilter = ({ value, onChange }) => (
    <div className="flex items-center border border-white/10 rounded-sm overflow-hidden" data-testid="users-country-filter">
        {["ALL", "NZ", "AU"].map((k) => (
            <button
                key={k}
                onClick={() => onChange(k)}
                className={`px-3 py-1.5 font-mono-data text-[11px] uppercase tracking-widest transition-colors ${
                    value === k ? "bg-[#22C55E] text-[#050505]" : "text-[#A1A1AA] hover:text-[#FAFAFA]"
                }`}
                data-testid={`users-country-filter-${k.toLowerCase()}`}
            >
                {k}
            </button>
        ))}
    </div>
);

const Skeleton = () => (
    <div className="p-8 space-y-4">
        <div className="h-12 w-80 bg-white/5 rounded-sm animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="hg-card h-[120px] animate-pulse" />)}
        </div>
        <div className="hg-card h-[460px] animate-pulse" />
    </div>
);
